import omit from 'lodash/omit';
import unionBy from 'lodash/unionBy';
import { Automation } from 'api/automations';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { HttpClient } from 'api/http-client';
import { DateType, Team, Timestamp, User } from 'api/models';
import { DependenciesMap } from 'api/project';
import { ConditionalVariablesForm } from 'components/molecules/variables/types';
import { DocumentTemplate } from '../documents/types';
import {
  CreateOrUpdateProjectTemplateApiRequest,
  CustomProjectTemplateTask,
  ProjectTemplateApi,
  ProjectTemplateProjectPhase,
} from '../projects/types';
import { StatusUpdateTemplate } from '../status-updates/type';
import { TaskTemplate, TaskTemplateLight } from '../tasks';

export enum TemplateState {
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
}

export type AutomationLiteForTemplate = Pick<
  Automation,
  | 'automationId'
  | 'automationName'
  | 'createdBy'
  | 'automationType'
  | 'triggerV2'
  | 'source'
  | 'sourceId'
  | 'createdAt'
  | 'updatedAt'
  | 'accountId'
  | 'deleted'
  | 'enabled'
  | 'mirrorFromTemplateId'
  | 'createdFromTemplateId'
  | 'tags'
>;

export enum TemplateSourceType {
  Template = 'TEMPLATE',
}

export type TemplateId = number | 'new'; // TODO

export enum TemplateIdType {
  Number = 'number',
  New = 'new',
}

export interface BaseTemplate {
  createdAt?: Timestamp;
  createdBy?: User;
  state: TemplateState;
  templateId: TemplateId;
  templateName?: string;
  publishedAt?: Timestamp;
  publishedBy?: User;
  objectType: TemplateObjectType;
  account?: { accountId: number };
}

export enum TemplateObjectType {
  Document = 'DOCUMENT',
  Project = 'PROJECT',
  Milestone = 'MILESTONE',
  Task = 'TASK',
  StatusUpdates = 'STATUS_UPDATE',
  Form = 'FORM',
  Automations = 'AUTOMATIONS',
  BillingSchedule = 'BILLING_SCHEDULE',
  Sheets = 'SHEETS',
  ResourceAllocation = 'RESOURCE_ALLOCATION',
}
export type Template =
  | DocumentTemplate
  | ProjectTemplateApi
  | TaskTemplate
  | StatusUpdateTemplate
  | CreateOrUpdateProjectTemplateApiRequest;

export type DynamicProjectCustomField = {
  fieldId: string;
  fieldValue: any;
};

export type DynamicProjectFields = {
  annualizedRecurringRevenue?: number;
  projectFee?: number;
  fields?: DynamicProjectCustomField[];
};

export type ProjectTemplatePreview = {
  templateRequest: Pick<
    ProjectTemplateApi,
    'templateContent' | 'objectType' | 'compressTimeline'
  >;
  project: DynamicProjectFields;
};

export interface TemplateDueDatePayload {
  templateId: number;
  startDate: DateType;
}

export interface TemplateDueDateResponse {
  startDate: DateType;
  dueDate: DateType;
}

export type TemplateMeta = {
  fieldFilters?: Omit<SimpleSearchFilter, 'match'>;
  aiFillAppIds?: number[];
  assignee: {
    teams: Team[];
  };
  compressTimeline?: boolean;
  conditionalVariables: {
    [id: string]: ConditionalVariablesForm;
  };
};

interface AllPhases {
  [key: string]: ProjectTemplateProjectPhase;
}

interface AllTasks {
  [key: string]: CustomProjectTemplateTask;
}

export interface DependenciesMapGenerationPayload {
  templateId: number;
  allTasks: AllTasks;
  allPhases: AllPhases;
}

const NAMESPACE = '/templates';

export class TemplatesHttpClient extends HttpClient {
  public getAllTemplates(): Promise<Template[]> {
    return this.getStatusUpdateTemplates().then(async (statusTemplates) => {
      const templates = await this.get<Template[]>(NAMESPACE);

      // only Status update templates returns an extra key, enableAutoPublish.
      // Now we're decorating the response, so that this can be maintained easily.
      const decoratedTemplates = unionBy(
        statusTemplates,
        templates,
        'templateId',
      );

      return decoratedTemplates;
    });
  }

  public async getUserCreatedTaskTemplates(): Promise<TaskTemplateLight[]> {
    const templates = await this.get<TaskTemplateLight[]>(
      `${NAMESPACE}/tasks/get-user-created`,
    );

    return templates;
  }

  public getStatusUpdateTemplates(): Promise<Template[]> {
    return this.get<Template[]>(`${NAMESPACE}/status-updates`);
  }

  public getStatusUpdateNextPublish(templateId: number): Promise<{
    nextPublishAt: Timestamp;
  }> {
    return this.get<{
      nextPublishAt: Timestamp;
    }>(`${NAMESPACE}/${templateId}/next-publish-at`);
  }

  public getById<T>(templateId: number): Promise<T> {
    return this.get<T>(`${NAMESPACE}/${templateId}`);
  }

  public deleteById<T>(templateId: number): Promise<T> {
    return this.delete<T>(`${NAMESPACE}/${templateId}`);
  }

  public create<T extends Template>(template: T): Promise<T> {
    return this.post<T, Omit<T, 'templateId'>, T>(
      `${NAMESPACE}`,
      omit(template, 'templateId'),
    );
  }

  public cloneById(templateId: number): Promise<Template> {
    return this.post(`${NAMESPACE}/${templateId}/clone`, {
      templateId,
    });
  }

  public save<T extends Template>(template: T): Promise<T> {
    if (template.templateId === 'new') {
      return this.create(template);
    }

    return this.put<T, T>(`${NAMESPACE}/${template.templateId}`, template);
  }

  public wipe<T>(templateId: number): Promise<T> {
    return this.delete<T>(`${NAMESPACE}/${templateId}`);
  }

  public preview<T extends ProjectTemplatePreview>(template: T): Promise<T> {
    return this.post<T, T>(`${NAMESPACE}/project-preview`, template);
  }

  public getDueDate({
    templateId,
    ...restPayload
  }: TemplateDueDatePayload): Promise<TemplateDueDateResponse> {
    return this.post(`${NAMESPACE}/${templateId}/get-due-date`, restPayload);
  }

  public getTemplateDependenciesMap(
    payload: DependenciesMapGenerationPayload,
  ): Promise<DependenciesMap> {
    return this.post(`${NAMESPACE}/dependency-map`, payload);
  }
}

export const TemplatesAPI = new TemplatesHttpClient();
